import * as service from "azure-maps-rest";

const es = global.es;

const MAPBOX_REVERSE_GEOCODE_URL: string = "https://api.mapbox.com/search/geocode/v6/reverse";
const MAPBOX_REVERSE_GEOCODE_TIMEOUT_MS: number = 3000;

/**
 * Geocoding module backed by Azure Maps for forward geocoding (address -> coordinates)
 * and Mapbox for reverse geocoding (coordinates -> address).
 *
 * Azure Maps Search: https://docs.microsoft.com/en-gb/javascript/api/azure-maps-rest/atlas.service.searchurl?view=azure-iot-typescript-latest
 * Mapbox Geocoding v6: https://docs.mapbox.com/api/search/geocoding/
 */
export default class AzureAndMapboxGeocodeModule {
    pipeline: service.Pipeline;
    searchURL: service.SearchURL;
    addressComponents: Record<string, string[]>;

    constructor() {
        const subscriptionKeyCredential = new service.SubscriptionKeyCredential(es.azureMapsKey);
        this.pipeline = service.MapsURL.newPipeline(subscriptionKeyCredential, {
            retryOptions: { maxTries: 4 } // Retry options
        });
        this.searchURL = new service.SearchURL(this.pipeline);

        // reverse geocode type -> attributes to map to
        this.addressComponents = {
            'country': ['country'],
            'region': ['state', 'region'],
            'postcode': ['zip', 'postcode', 'postalcode', 'postal code', 'post_cd'],
            'district': ['county', 'region'],
            'place': ['city', 'town', 'cty'],
            'locality': ['city', 'region', 'town'],
            'neighborhood': ['city', 'town'],
            'street': ['address', 'addr'],
            'address': ['address', 'addr'],
            'secondary_address': ['address', 'addr']
        };
    }

    /**
     * Address Geocoding
     * https://docs.microsoft.com/en-gb/javascript/api/azure-maps-rest/atlas.service.searchurl?view=azure-iot-typescript-latest#searchaddress-aborter--string--searchaddressoptions-
     * 
     * @param addressStr
     * @param countryCode
     * 
     * @returns {JQuery.Promise<any, any, any>}
     */
    geocode(addressStr: string,  countryCode: string): JQuery.Promise<any, any, any> {
        if (addressStr.trim() == '') {
            return es.util.deferred.rejected();
        }
        
        const dfd = $.Deferred();
        
        this.searchURL.searchAddress(service.Aborter.timeout(10000), addressStr, {'countrySet': [countryCode]}).then((response) => {
            const results = response.results;
            const feature = results[0];
            
            if (!feature) {
                return dfd.reject();
            }
            
            const geometry =  {'location': [feature.position.lon, feature.position.lat]};
            const address = this.formatAddress(this.parseAddress(feature.address));
            dfd.resolve(geometry, address);
        });
        
        return dfd.promise();
    };

    /**
     * Reverse Geocode to an Address, using the Mapbox Geocoding v6 reverse endpoint.
     * https://docs.mapbox.com/api/search/geocoding/#reverse-geocoding
     *
     * @param lonLat
     *
     * @returns {JQuery.Promise<any, any, any>}
     */
    reverseGeocode(lonLat: number[]): JQuery.Promise<any, any, any> {
        const dfd = $.Deferred();

        let latitude = lonLat[1];
        let longitude = lonLat[0];

        // Normalize longitude into [-180, 180], handling any number of antimeridian wraps.
        longitude = ((longitude + 180) % 360 + 360) % 360 - 180;

        if (!es.mapboxAccessToken || es.mapboxAccessToken === "n/a") {
            es.log.warn("AzureAndMapboxGeocodeModule: Mapbox reverse geocoding is not configured");
            return es.util.deferred.rejected();
        }

        (async () => {
            const url = MAPBOX_REVERSE_GEOCODE_URL
                + "?longitude=" + longitude
                + "&latitude=" + latitude
                + "&access_token=" + es.mapboxAccessToken
                + "&permanent=" + es.mapboxGeocoderPermanent;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), MAPBOX_REVERSE_GEOCODE_TIMEOUT_MS);

            let mapboxData;
            try {
                const mapboxRes = await fetch(url, { signal: controller.signal });
                mapboxData = await mapboxRes.json();
            } finally {
                clearTimeout(timeoutId);
            }

            const mbFeature = mapboxData.features && mapboxData.features[0];
            if (!mbFeature) {
                return dfd.reject();
            }

            const props = mbFeature.properties;
            const mbRS = props.full_address || props.place_formatted || props.name;

            const context = { ...(props.context || {}) };
            if (props.feature_type && props.name && !context[props.feature_type]) {
                context[props.feature_type] = { name: props.name };
            }

            const mbAddressComponents = Object.keys(context).reduce((a, addressType) => {
                if (!this.addressComponents[addressType]) {
                    // type we don't have a column mapping for - skip it
                    return a;
                }

                const addressValue = context[addressType].name;
                if (!addressValue) {
                    return a;
                }

                a.address.push(addressValue);
                a.addressTypes.push(addressType);
                return a;
            }, {address: [] as string[], addressTypes: [] as string[]});

            dfd.resolve(mbRS, mbAddressComponents);
        })().catch(() => dfd.reject());

        return dfd.promise();
    };

    /**
     * 
     * @param addressComponents 
     * 
     * @returns 
     */
    private formatAddress(addressComponents) {
        let address = addressComponents["address"][addressComponents["addressTypes"].indexOf("freeformAddress")];
        address = address + ", " + addressComponents["address"][addressComponents["addressTypes"].indexOf("country")];

        return address;
    }

    /**
     * 
     * @param address_components 
     * 
     * @returns 
     */
    private parseAddress(address_components) {
        let addressDetail;
        for (let addressComponent in address_components) {
            if (address_components.hasOwnProperty(addressComponent)) {
                const addrType = addressComponent;
                const addrValue = address_components[addressComponent];

                if (addressDetail) {
                    const addressType = this.isAddressType(addressComponent);

                    if (addressType && addressDetail.addressTypes.indexOf(addressType) > -1) {
                        var mergedAddress = addrValue;
                        var currentAddressIndex = addressDetail.addressTypes.indexOf(addressType);
                        addressDetail.address[currentAddressIndex] = addressDetail.address[currentAddressIndex] + " " + mergedAddress;
                    } else {
                        addressDetail.address.push(addrValue);
                        addressDetail.addressTypes.push(addressType ? addressType : addrType);
                    }
                } else {
                    const type = this.isAddressType(addressComponent);

                    addressDetail = {
                        addressTypes: [type ? type : addrType],
                        address: [addrValue]
                    };
                }
            }
        }
        return addressDetail;
    }

    /**
     * 
     * @param address_type 
     * 
     * @returns 
     */
    private isAddressType(address_type: string): string | null {
        const azureAddressTypes = ['streetNumber', 'streetName'];
        return azureAddressTypes.includes(address_type) ? 'address' : null;
    }
};
